"use client"

import { useMemo, useState } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { AlertCircle, ArrowLeft, BarChart3, Loader2, Mail, RefreshCcw, Settings } from "lucide-react"
import { toast } from "sonner"

import { QAFeedbackLinkButton } from "@/components/ai/feedback/qa-feedback-link-button"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { ConversationEmptyState } from "@/components/ui/conversation/conversation-empty-state"
import { InboxFilterBar, type FixedInboxControl } from "@/components/ui/conversation/inbox-filter-bar"
import { InboxHeaderButton, InboxLayout } from "@/components/ui/conversation/inbox-layout"
import { InboxRow } from "@/components/ui/conversation/inbox-row"
import { InboxThreadPane } from "@/components/ui/conversation/inbox-thread-pane"
import { AutoReplyReviewDialog } from "@/components/agents/email/dialogs"
import { InfiniteScrollSentinel } from "@/components/ui/infinite-scroll-sentinel"
import { PageLoader } from "@/components/ui/page-loader"
import { useEmailAgents, type EmailAgent } from "@/hooks/agents/email/use-agents"
import { useEmailMessage } from "@/hooks/agents/email/use-email-message"
import { useAuth } from "@/hooks/auth/use-auth"
import {
  useInboxFilterOptions,
  useInboxSourceCapabilities,
  useUnifiedInbox,
  useUnifiedThread,
} from "@/hooks/queries/unified-inbox"
import { useInboxSelection } from "@/hooks/use-inbox-selection"
import { usePermissions } from "@/hooks/use-permissions"
import { EMAIL_AGENTS_PERMISSIONS, EMAIL_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { resolveUserTimezone } from "@/lib/date-utils"
import { scopedId, sourceOfScopedValue, threadKey } from "@/lib/inbox/scoped-id"
import type { InboxFilterOptions, UnifiedInboxFilters } from "@/lib/inbox/types"
import { AgentStatusBadge } from "../agent-status-badge"

/** Handover mail and Telegram already carry this. It names a message, not a thread. */
const LEGACY_KEYS = ["emailExternalId"] as const
const EMAIL_CHANNEL_TYPE = scopedId("email", "email")
/** The page names its agent and its source; the bar and the rows need not repeat them. */
const FIXED_CONTROLS: readonly FixedInboxControl[] = ["agent", "channelType"]
const SURFACE_OWNS = ["source", "agent"] as const

/**
 * One email agent's threads, read through the unified inbox's email source with
 * the agent as a fixed scope. Nothing here is a second copy of the list or the
 * thread — this page only decides what the tenant-wide inbox leaves open.
 */
export function EmailAgentInboxPage() {
  const router = useRouter()
  const agentId = useSearchParams().get("agentId")
  const { user, fetchWithAuth } = useAuth()
  const { can } = usePermissions()
  const timezone = useMemo(() => resolveUserTimezone(user), [user])
  const { agents, isLoading: isLoadingAgents } = useEmailAgents()
  const agent = useMemo(() => agents.find((candidate) => candidate.id === agentId), [agents, agentId])
  const scope = agentId ? scopedId("email", agentId) : null

  const { fetchDetail } = useEmailMessage()
  const { selectedId: urlSelectedId, select, error: selectionError } = useInboxSelection({
    legacyKeys: LEGACY_KEYS,
    resolveLegacy: async (_key, externalId) => {
      const detail = await fetchDetail(externalId, agentId ?? undefined)
      return detail.inboxId && detail.threadId
        ? scopedId("email", threadKey(detail.inboxId, detail.threadId))
        : null
    },
  })

  const [filters, setFilters] = useState<UnifiedInboxFilters>({})
  const capabilities = useInboxSourceCapabilities()
  const emailCapabilities = useMemo(
    () => (capabilities.data ?? []).filter((source) => source.kind === "email"),
    [capabilities.data],
  )
  const filterOptions = useInboxFilterOptions()
  const options = useMemo(
    () => scopeFilterOptions(filterOptions.data ?? [], agent),
    [filterOptions.data, agent],
  )

  // Merged here and nowhere else: the bar's «Clear all» resets its own object to `{}`,
  // and these two values are the page's, not the operator's.
  const list = useUnifiedInbox(
    { ...filters, channelTypes: [EMAIL_CHANNEL_TYPE], agent: scope ?? undefined },
    { enabled: !!scope },
  )
  const items = useMemo(() => list.data?.pages.flatMap((page) => page.items) ?? [], [list.data])
  const failedSources = list.data?.pages.at(-1)?.failedSources ?? []
  // A conversation of another source is not this page's to open.
  const selectedId =
    scope && urlSelectedId && sourceOfScopedValue(urlSelectedId) === "email" ? urlSelectedId : null
  const selected = items.find((item) => item.id === selectedId) ?? null
  const thread = useUnifiedThread(selectedId, { agent: scope ?? undefined })
  const [reviewingReplyId, setReviewingReplyId] = useState<string | null>(null)

  const [isSyncing, setIsSyncing] = useState(false)
  const syncInboxes = async () => {
    if (!user?.uuid || !agent?.inboxes.length) {
      toast.error("No inboxes to sync")
      return
    }
    setIsSyncing(true)
    try {
      let synced = 0
      for (const inbox of agent.inboxes) {
        const res = await fetchWithAuth("/api/email/sync", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_uuid: user.uuid, email: inbox.address, agent_id: agent.id, maxResults: 50 }),
        })
        if (res.ok) synced += (await res.json()).syncedCount || 0
      }
      toast.success(`Synced ${synced} emails`)
      void list.refetch()
    } catch (err) {
      console.error("Sync error:", err)
      toast.error("Failed to sync emails")
    } finally {
      setIsSyncing(false)
    }
  }

  const goBack = () => router.push("?page=email-agents")

  if (isLoadingAgents) {
    return (
      <div className="mx-auto max-w-6xl">
        <PageLoader message="Loading agent..." />
      </div>
    )
  }
  if (!can(EMAIL_PERMISSIONS.VIEW)) {
    return <BlockedPage message="You don't have permission to view emails" onBack={goBack} />
  }
  if (!agent || !scope) {
    return <BlockedPage message="Agent not found" onBack={goBack} />
  }

  const canSync = can(EMAIL_PERMISSIONS.SYNC)
  const inboxCount = agent.inboxes.length

  return (
    <InboxLayout
      hasSelection={!!selectedId}
      header={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 basis-80 items-center gap-2 split:gap-4">
            <InboxHeaderButton
              variant="ghost"
              size="sm"
              className="shrink-0 px-2 split:px-3"
              icon={<ArrowLeft className="h-4 w-4 split:mr-2" />}
              label="Back"
              onClick={goBack}
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="min-w-0 text-lg font-semibold [overflow-wrap:anywhere] split:text-2xl">{agent.name}</h1>
                <AgentStatusBadge status={agent.status} />
              </div>
              <p className="mt-1 hidden text-sm text-muted-foreground split:block">
                {`${inboxCount} ${inboxCount === 1 ? "inbox" : "inboxes"} connected`}
              </p>
            </div>
          </div>
          <div className="ml-auto flex max-w-full flex-wrap items-center gap-2">
            {canSync && (
              <InboxHeaderButton
                icon={
                  isSyncing ? (
                    <Loader2 className="h-4 w-4 animate-spin split:mr-2" />
                  ) : (
                    <RefreshCcw className="h-4 w-4 split:mr-2" />
                  )
                }
                label={isSyncing ? "Syncing..." : "Sync Emails"}
                onClick={syncInboxes}
                disabled={isSyncing || inboxCount === 0}
              />
            )}
            {can(EMAIL_PERMISSIONS.QA_FEEDBACK) && (
              // Not on a phone, by decision: the page it opens is a desk job.
              <QAFeedbackLinkButton
                agentId={agent.id}
                agentType="email_agent"
                agentName={agent.name}
                pendingOnly={false}
                className="hidden split:inline-flex"
              />
            )}
            {can(EMAIL_AGENTS_PERMISSIONS.ANALYTICS) && (
              <InboxHeaderButton
                variant="outline"
                icon={<BarChart3 className="h-4 w-4 split:mr-2" />}
                label="Analytics"
                onClick={() => router.push(`?page=agent-analytics&agentId=${agent.id}`)}
              />
            )}
            {can(EMAIL_AGENTS_PERMISSIONS.MANAGE) && (
              <InboxHeaderButton
                variant="outline"
                icon={<Settings className="h-4 w-4 split:mr-2" />}
                label="Settings"
                onClick={() => router.push(`?page=email-agents&agentId=${agent.id}`)}
              />
            )}
          </div>
        </div>
      }
      list={
        <>
          <InboxFilterBar
            filters={filters}
            capabilities={emailCapabilities}
            options={options}
            optionsError={(filterOptions.error as Error | null) ?? null}
            onChange={setFilters}
            fixed={FIXED_CONTROLS}
          />

          {selectionError && (
            <p className="border-b bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {selectionError}
            </p>
          )}

          <div className="flex-1 overflow-y-auto">
            {failedSources.map((failure) => (
              <div
                key={failure.kind}
                className="flex items-start gap-2 border-b bg-destructive/10 px-3 py-2 text-xs"
              >
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-destructive" />
                <p className="min-w-0 flex-1 truncate">{failure.message}</p>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => list.refetch()}>
                  Retry
                </Button>
              </div>
            ))}

            {list.isLoading && <PageLoader />}

            {list.error && !list.isLoading && (
              <p className="p-4 text-sm text-destructive">{(list.error as Error).message}</p>
            )}

            {!list.isLoading && !list.error && items.length === 0 && failedSources.length === 0 && (
              <EmptyList
                hasInbox={inboxCount > 0}
                onSync={canSync ? syncInboxes : undefined}
                syncing={isSyncing}
              />
            )}

            {items.map((item) => (
              <InboxRow
                key={item.id}
                item={item}
                isActive={item.id === selectedId}
                onClick={() => select(item.id)}
                timezone={timezone}
                surfaceOwns={SURFACE_OWNS}
              />
            ))}

            <InfiniteScrollSentinel
              hasMore={list.hasNextPage}
              isLoading={list.isFetchingNextPage}
              hasFailed={list.isFetchNextPageError}
              onLoadMore={list.fetchNextPage}
            />
          </div>
        </>
      }
      pane={
        selectedId ? (
          <>
            <InboxThreadPane
              key={selectedId}
              item={selected}
              thread={thread.data}
              isLoading={thread.isLoading}
              error={(thread.error as Error | null) ?? null}
              timezone={timezone}
              onBack={() => select(null)}
              onQaFeedback={setReviewingReplyId}
            />
            <AutoReplyReviewDialog
              autoReplyId={reviewingReplyId}
              agentId={agent.id}
              onClose={() => setReviewingReplyId(null)}
            />
          </>
        ) : (
          <ConversationEmptyState
            title="Pick a conversation"
            description="Every thread this agent handles opens here, ready to answer."
          />
        )
      }
    />
  )
}

/**
 * The email group only, narrowed to the mailboxes this agent actually has: the
 * options are tenant-wide while the list is agent-scoped, and a mailbox of another
 * agent would be a guaranteed-empty list. One mailbox is no choice at all.
 */
function scopeFilterOptions(groups: InboxFilterOptions[], agent: EmailAgent | undefined): InboxFilterOptions[] {
  const email = groups.find((group) => group.source === "email")
  if (!email || !agent) return []
  const own = new Set(agent.inboxes.map((inbox) => scopedId("email", inbox.id)))
  const channels = email.channels.filter((channel) => own.has(channel.id))
  return [{ ...email, agents: [], channels: channels.length > 1 ? channels : [] }]
}

function BlockedPage({ message, onBack }: { message: string; onBack: () => void }) {
  return (
    <div className="mx-auto max-w-6xl p-6">
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{message}</AlertDescription>
      </Alert>
      <Button onClick={onBack} className="mt-4">
        <ArrowLeft className="mr-2 h-4 w-4" />
        <span>Back to Agents</span>
      </Button>
    </div>
  )
}

function EmptyList({
  hasInbox,
  syncing,
  onSync,
}: {
  hasInbox: boolean
  syncing: boolean
  onSync?: () => void
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
      <Mail className="mb-4 h-12 w-12 text-gray-300" />
      <p className="text-lg font-medium">No emails found</p>
      <p className="mt-1 text-sm">{hasInbox ? "Try syncing or adjusting filters" : "Connect an inbox first"}</p>
      {hasInbox && onSync && (
        <Button className="mt-4" onClick={onSync} disabled={syncing}>
          <RefreshCcw className="mr-2 h-4 w-4" />
          <span>Sync Emails</span>
        </Button>
      )}
    </div>
  )
}
